import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { toast } from "sonner"

import { AdminPageHeader } from "@/components/AdminPageHeader"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { api } from "@/lib/client"
import { queryKeys } from "@/lib/queryKeys"
import { ADMIN_TABLE_CLASSNAME, ADMIN_TABLE_HEAD_TH } from "@/lib/table"
import { cn } from "@/lib/utils"

export function StockPage() {
  const queryClient = useQueryClient()
  const [q, setQ] = useState("")
  const [adjust, setAdjust] = useState<{ name: string; productId: number } | null>(null)
  const [qty, setQty] = useState("0")
  const [reason, setReason] = useState("")
  const stock = useQuery({ queryFn: () => api.stock(), queryKey: queryKeys.stock })
  const rows = useMemo(() => {
    const list = stock.data?.balances ?? []
    const needle = q.trim().toLowerCase()
    if (!needle) return list
    return list.filter(
      (b) => b.name.toLowerCase().includes(needle) || b.sku.toLowerCase().includes(needle),
    )
  }, [q, stock.data])

  const mutation = useMutation({
    mutationFn: () =>
      api.adjustStock({
        productId: adjust!.productId,
        quantity: Number(qty),
        reason: reason || undefined,
      }),
    onError: (err) => toast.error(err.message),
    onSuccess: async () => {
      toast.success("Stock adjusted")
      setAdjust(null)
      await queryClient.invalidateQueries({ queryKey: queryKeys.stock })
    },
  })

  return (
    <div>
      <AdminPageHeader description="On-hand balances. Adjustments post immediately." title="Stock">
        <Input className="w-56" onChange={(e) => setQ(e.target.value)} placeholder="Search" value={q} />
      </AdminPageHeader>
      <div className="overflow-hidden rounded-lg border border-border">
        <Table className={ADMIN_TABLE_CLASSNAME}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Product</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>SKU</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH}>Lot</TableHead>
              <TableHead className={`${ADMIN_TABLE_HEAD_TH} text-right`}>On hand</TableHead>
              <TableHead className={`${ADMIN_TABLE_HEAD_TH} text-right`}>Reserved</TableHead>
              <TableHead className={ADMIN_TABLE_HEAD_TH} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {stock.isPending ? (
              <TableRow>
                <TableCell className="text-muted-foreground" colSpan={6}>
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell className="text-muted-foreground" colSpan={6}>
                  No stock rows yet. Receive a lot or adjust a product.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={`${row.productId}-${row.lotId ?? 0}`}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell className="text-muted-foreground">{row.sku}</TableCell>
                  <TableCell>{row.lotId ?? "—"}</TableCell>
                  <TableCell className={cn("text-right", row.qtyOnHand < 0 && "text-amber-300")}>
                    {row.qtyOnHand}
                  </TableCell>
                  <TableCell className="text-right">{row.qtyReserved}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      onClick={() => {
                        setAdjust({ name: row.name, productId: row.productId })
                        setQty("0")
                        setReason("")
                      }}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      Adjust
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog onOpenChange={(open) => !open && setAdjust(null)} open={Boolean(adjust)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust {adjust?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="qty">Quantity (+/−)</Label>
              <Input id="qty" onChange={(e) => setQty(e.target.value)} type="number" value={qty} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Input id="reason" onChange={(e) => setReason(e.target.value)} value={reason} />
            </div>
          </div>
          <DialogFooter>
            <Button disabled={mutation.isPending} onClick={() => mutation.mutate()} type="button">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
